import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FaDesktop, FaPlus, FaRedo, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import {
    approveRdpRequest,
    createRdpAccountJob,
    createRdpManualRequest,
    createRdpProxy,
    createRdpServer,
    deleteRdpAccount,
    deleteRdpJob,
    deleteRdpProxy,
    fetchRdpAdminOverview,
    rejectRdpRequest,
    runRdpJobDryRun,
    runRdpJobLive,
    updateRdpAccount,
    updateRdpProxy,
    updateRdpServer,
} from "../../http/rdpAPI";

const EMPTY_SERVER = {
    label: "",
    host: "",
    port: "3389",
    status: "ACTIVE",
    max_accounts: "",
    notes: "",
};

const EMPTY_PROXY = {
    label: "",
    host: "",
    port: "",
    username: "",
    password: "",
    country: "",
    status: "FREE",
    notes: "",
};

const EMPTY_APPROVE = {
    request_id: "",
    rdp_server_id: "",
    rdp_proxy_id: "",
    windows_username: "",
    windows_password: "",
    account_status: "MANUAL_REQUIRED",
    admin_note: "",
};

const PAGE_SIZE = 10;

const generateWindowsPassword = () => {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const all = lower + upper + digits;
    const required = [lower, upper, digits].map((chars) => chars[Math.floor(Math.random() * chars.length)]);

    for (let i = required.length; i < 10; i += 1) {
        required.push(all[Math.floor(Math.random() * all.length)]);
    }

    return required
        .map((char) => ({ char, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map((item) => item.char)
        .join("");
};

const badgeVariant = (status) => {
    switch (status) {
        case "ACTIVE":
        case "APPROVED":
        case "FREE":
        case "DONE":
            return "success";
        case "PENDING":
        case "MANUAL_REQUIRED":
        case "QUEUED":
        case "DRY_RUN":
            return "warning";
        case "ASSIGNED":
            return "primary";
        case "DISABLED":
        case "REJECTED":
            return "secondary";
        case "BROKEN":
        case "DELETED":
        case "FAILED":
            return "danger";
        case "CANCELED":
            return "dark";
        default:
            return "light";
    }
};

const renderUser = (user) => {
    if (!user) return "No user";
    return `${user.username || user.email || "User"} #${user.id} (${user.tariff_plan || "?"})`;
};

const pageItems = (items, page) => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
};

const PaginationControls = ({ page, total, onPage }) => {
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (total <= PAGE_SIZE) return null;

    return (
        <div className="d-flex align-items-center justify-content-end gap-2 mt-2 small">
            <span className="text-muted">
                Page {page} / {pages}
            </span>
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
                Prev
            </Button>
            <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>
                Next
            </Button>
        </div>
    );
};

const AdminRdpAccess = () => {
    const [data, setData] = useState({ servers: [], proxies: [], requests: [], accounts: [], jobs: [] });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [actionKey, setActionKey] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [serverForm, setServerForm] = useState(EMPTY_SERVER);
    const [proxyForm, setProxyForm] = useState(EMPTY_PROXY);
    const [editingServerId, setEditingServerId] = useState(null);
    const [editingProxyId, setEditingProxyId] = useState(null);
    const [manualUserId, setManualUserId] = useState("");
    const [manualNote, setManualNote] = useState("");
    const [approveForm, setApproveForm] = useState(EMPTY_APPROVE);
    const [requestPage, setRequestPage] = useState(1);
    const [accountPage, setAccountPage] = useState(1);
    const [jobPage, setJobPage] = useState(1);

    const freeProxies = useMemo(
        () => data.proxies.filter((p) => p.status === "FREE" || String(p.id) === String(approveForm.rdp_proxy_id)),
        [data.proxies, approveForm.rdp_proxy_id]
    );

    const visibleRequests = useMemo(() => pageItems(data.requests, requestPage), [data.requests, requestPage]);
    const visibleAccounts = useMemo(() => pageItems(data.accounts, accountPage), [data.accounts, accountPage]);
    const visibleJobs = useMemo(() => pageItems(data.jobs, jobPage), [data.jobs, jobPage]);

    const load = async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError("");
        }
        try {
            const overview = await fetchRdpAdminOverview();
            setData({
                servers: overview.servers || [],
                proxies: overview.proxies || [],
                requests: overview.requests || [],
                accounts: overview.accounts || [],
                jobs: overview.jobs || [],
            });
            setRequestPage((page) => Math.min(page, Math.max(1, Math.ceil((overview.requests || []).length / PAGE_SIZE))));
            setAccountPage((page) => Math.min(page, Math.max(1, Math.ceil((overview.accounts || []).length / PAGE_SIZE))));
            setJobPage((page) => Math.min(page, Math.max(1, Math.ceil((overview.jobs || []).length / PAGE_SIZE))));
        } catch (e) {
            setError(e?.response?.data?.message || e.message || "Failed to load RDP data.");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const run = async (fn, message, key = "global") => {
        setSaving(true);
        setActionKey(key);
        setError("");
        setSuccess("");
        try {
            await fn();
            if (message) setSuccess(message);
            await load({ silent: true });
        } catch (e) {
            const messageText = e?.response?.data?.message || e.message || "RDP action failed.";
            toast.error(messageText, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setSaving(false);
            setActionKey("");
        }
    };

    const isAction = (key) => actionKey === key;
    const ActionSpinner = ({ show }) => (show ? <Spinner animation="border" size="sm" className="me-1" /> : null);

    const saveServer = async () => {
        await run(async () => {
            if (editingServerId) {
                await updateRdpServer(editingServerId, serverForm);
            } else {
                await createRdpServer(serverForm);
            }
            setServerForm(EMPTY_SERVER);
            setEditingServerId(null);
        }, "Server saved.", editingServerId ? `server-${editingServerId}` : "server-new");
    };

    const editServer = (server) => {
        setEditingServerId(server.id);
        setServerForm({
            label: server.label || "",
            host: server.host || "",
            port: String(server.port || 3389),
            status: server.status || "ACTIVE",
            max_accounts: server.max_accounts || "",
            notes: server.notes || "",
        });
    };

    const saveProxy = async () => {
        await run(async () => {
            if (editingProxyId) {
                await updateRdpProxy(editingProxyId, proxyForm);
            } else {
                await createRdpProxy(proxyForm);
            }
            setProxyForm(EMPTY_PROXY);
            setEditingProxyId(null);
        }, "Proxy saved.", editingProxyId ? `proxy-${editingProxyId}` : "proxy-new");
    };

    const editProxy = (proxy) => {
        setEditingProxyId(proxy.id);
        setProxyForm({
            label: proxy.label || "",
            host: proxy.host || "",
            port: String(proxy.port || ""),
            username: proxy.username || "",
            password: proxy.password || "",
            country: proxy.country || "",
            status: proxy.status || "FREE",
            notes: proxy.notes || "",
        });
    };

    const createManualRequest = async () => {
        await run(async () => {
            await createRdpManualRequest({ user_id: manualUserId, admin_note: manualNote });
            setManualUserId("");
            setManualNote("");
        }, "Manual request created.", "request-manual");
    };

    const startApprove = (request) => {
        const firstServer = data.servers.find((s) => s.status === "ACTIVE") || data.servers[0];
        const firstProxy = data.proxies.find((p) => p.status === "FREE");
        if (!firstProxy) {
            toast.error("No free RDP proxy available. Add or free a proxy before approving this request.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }
        const userNamePart = request?.user?.username || request?.user?.email || `user${request.user_id}`;
        const cleanName = String(userNamePart).replace(/[^a-zA-Z0-9]/g, "").slice(0, 8) || `u${request.user_id}`;
        const windowsUsername = `vl${request.user_id}_${cleanName}`.slice(0, 20);
        setApproveForm({
            request_id: String(request.id),
            rdp_server_id: firstServer ? String(firstServer.id) : "",
            rdp_proxy_id: firstProxy ? String(firstProxy.id) : "",
            windows_username: windowsUsername,
            windows_password: generateWindowsPassword(),
            account_status: "MANUAL_REQUIRED",
            admin_note: "",
        });
    };

    const approveSelected = async () => {
        if (!approveForm.rdp_proxy_id) {
            toast.error("Select a free RDP proxy before approving this request.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }
        await run(async () => {
            await approveRdpRequest(approveForm.request_id, { ...approveForm, run_live: true });
            setApproveForm(EMPTY_APPROVE);
        }, "", `request-approve-${approveForm.request_id}`);
    };

    const rejectSelected = async (request) => {
        await run(async () => {
            await rejectRdpRequest(request.id, { admin_note: "Rejected by admin." });
        }, "", `request-reject-${request.id}`);
    };

    const setAccountStatus = async (account, status) => {
        await run(async () => {
            await updateRdpAccount(account.id, { status, run_live: ["DISABLED", "DELETED"].includes(status) });
        }, "", `account-${status.toLowerCase()}-${account.id}`);
    };

    const removeAccount = async (account) => {
        await run(async () => {
            await deleteRdpAccount(account.id, { run_live: true });
        }, "", `account-delete-${account.id}`);
    };

    const createWindowsUser = async (account) => {
        await run(async () => {
            await createRdpAccountJob(account.id, { action: "CREATE_WINDOWS_USER", run_live: true });
        }, "", `account-create-${account.id}`);
    };

    const lockdownWindowsUser = async (account) => {
        await run(async () => {
            await createRdpAccountJob(account.id, { action: "LOCKDOWN_WINDOWS_USER", run_live: true });
        }, "", `account-lockdown-${account.id}`);
    };

    const removeProxy = async (proxy) => {
        await run(async () => {
            await deleteRdpProxy(proxy.id);
        }, "", `proxy-delete-${proxy.id}`);
    };

    const rerunJob = async (job) => {
        await run(async () => {
            await runRdpJobDryRun(job.id);
        }, "", `job-dry-${job.id}`);
    };

    const runJobLive = async (job) => {
        await run(async () => {
            await runRdpJobLive(job.id);
        }, "", `job-live-${job.id}`);
    };

    const removeJob = async (job) => {
        await run(async () => {
            await deleteRdpJob(job.id);
        }, "", `job-delete-${job.id}`);
    };

    const compactTableStyle = (
        <style>
            {`
                .rdp-admin-table {
                    font-size: 0.72rem;
                    line-height: 1.15;
                }
                .rdp-admin-table th,
                .rdp-admin-table td {
                    padding: 0.2rem 0.25rem;
                    vertical-align: middle;
                }
                .rdp-admin-table .form-control-sm,
                .rdp-admin-table .form-select-sm,
                .rdp-admin-table .btn-sm {
                    font-size: 0.72rem;
                    line-height: 1.15;
                    padding-top: 0.15rem;
                    padding-bottom: 0.15rem;
                }
                .rdp-admin-table .badge {
                    font-size: 0.66rem;
                }
            `}
        </style>
    );

    const renderRequests = () => (
        <div className="border rounded p-2 mb-3">
            <div className="fw-semibold small mb-2">Requests</div>
            <div className="rdp-admin-table">
                <Table size="sm" bordered hover striped responsive className="mb-0">
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>User</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Server</th>
                        <th>Proxy</th>
                        <th>Win user</th>
                        <th>Password</th>
                        <th>Acc status</th>
                        <th>Note</th>
                        <th>Account</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {visibleRequests.map((request) => {
                        const isSelectedForApprove = String(approveForm.request_id) === String(request.id);
                        const isPending = request.status === "PENDING";
                        const isApproving = isSelectedForApprove && isPending;
                        const account = request.account || null;

                        return (
                            <tr key={request.id} className={isApproving ? "table-light" : ""}>
                                <td>{request.id}</td>
                                <td>{renderUser(request.user)}</td>
                                <td><Badge bg={badgeVariant(request.status)}>{request.status}</Badge></td>
                                <td>{new Date(request.createdAt).toLocaleString()}</td>
                                <td style={{ minWidth: 165 }}>
                                    {isApproving ? (
                                        <Form.Select size="sm" value={approveForm.rdp_server_id} onChange={(e) => setApproveForm({ ...approveForm, rdp_server_id: e.target.value })}>
                                            <option value="">Select</option>
                                            {data.servers.map((server) => (
                                                <option key={server.id} value={server.id}>{server.label} {server.host}</option>
                                            ))}
                                        </Form.Select>
                                    ) : (
                                        account?.server ? `${account.server.host}:${account.server.port}` : ""
                                    )}
                                </td>
                                <td style={{ minWidth: 140 }}>
                                    {isApproving ? (
                                        <Form.Select size="sm" value={approveForm.rdp_proxy_id} onChange={(e) => setApproveForm({ ...approveForm, rdp_proxy_id: e.target.value })}>
                                            <option value="">No proxy</option>
                                            {freeProxies.map((proxy) => (
                                                <option key={proxy.id} value={proxy.id}>{proxy.label || proxy.host}:{proxy.port}</option>
                                            ))}
                                        </Form.Select>
                                    ) : (
                                        account?.proxy ? `${account.proxy.host}:${account.proxy.port}` : ""
                                    )}
                                </td>
                                <td style={{ minWidth: 120 }}>
                                    {isApproving ? (
                                        <Form.Control size="sm" value={approveForm.windows_username} onChange={(e) => setApproveForm({ ...approveForm, windows_username: e.target.value })} />
                                    ) : (
                                        account?.windows_username || ""
                                    )}
                                </td>
                                <td style={{ minWidth: 155 }}>
                                    {isApproving ? (
                                        <div className="d-flex gap-1">
                                            <Form.Control size="sm" value={approveForm.windows_password} onChange={(e) => setApproveForm({ ...approveForm, windows_password: e.target.value })} />
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => setApproveForm({ ...approveForm, windows_password: generateWindowsPassword() })}
                                                title="Generate password"
                                            >
                                                <FaRedo />
                                            </Button>
                                        </div>
                                    ) : (
                                        account?.windows_password || ""
                                    )}
                                </td>
                                <td style={{ minWidth: 135 }}>
                                    {isApproving ? (
                                        <Form.Select size="sm" value={approveForm.account_status} onChange={(e) => setApproveForm({ ...approveForm, account_status: e.target.value })}>
                                            <option value="MANUAL_REQUIRED">MANUAL_REQUIRED</option>
                                            <option value="ACTIVE">ACTIVE</option>
                                        </Form.Select>
                                    ) : (
                                        account?.status ? <Badge bg={badgeVariant(account.status)}>{account.status}</Badge> : ""
                                    )}
                                </td>
                                <td style={{ minWidth: 160 }}>
                                    {isApproving ? (
                                        <Form.Control size="sm" placeholder="Admin note" value={approveForm.admin_note} onChange={(e) => setApproveForm({ ...approveForm, admin_note: e.target.value })} />
                                    ) : (
                                        request.admin_note || ""
                                    )}
                                </td>
                                <td>{account ? account.windows_username : ""}</td>
                                <td className="text-nowrap">
                                    {isPending && (
                                        isApproving ? (
                                            <>
                                                <Button size="sm" variant="success" className="me-1" onClick={approveSelected} disabled={Boolean(actionKey)}>
                                                    <ActionSpinner show={isAction(`request-approve-${approveForm.request_id}`)} />
                                                    Approve
                                                </Button>
                                                <Button size="sm" variant="outline-secondary" onClick={() => setApproveForm(EMPTY_APPROVE)} disabled={Boolean(actionKey)}>Cancel</Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button size="sm" variant="outline-success" className="me-1" onClick={() => startApprove(request)}>Create RDP</Button>
                                                <Button size="sm" variant="outline-danger" onClick={() => rejectSelected(request)} disabled={Boolean(actionKey)}>
                                                    <ActionSpinner show={isAction(`request-reject-${request.id}`)} />
                                                    Reject
                                                </Button>
                                            </>
                                        )
                                    )}
                                    {isSelectedForApprove && !isPending && (
                                        <Button size="sm" variant="outline-secondary" onClick={() => setApproveForm(EMPTY_APPROVE)} disabled={Boolean(actionKey)}>
                                            Cancel edit
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {data.requests.length === 0 && (
                        <tr><td colSpan={12} className="text-muted text-center">No RDP requests yet.</td></tr>
                    )}
                    </tbody>
                </Table>
            </div>
            <PaginationControls page={requestPage} total={data.requests.length} onPage={setRequestPage} />
        </div>
    );

    const renderAccounts = () => (
        <div className="border rounded p-2 mb-3">
            <div className="fw-semibold small mb-2">Accounts</div>
            <div className="rdp-admin-table">
                <Table size="sm" bordered hover striped responsive className="mb-0">
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>User</th>
                        <th>Server</th>
                        <th>Proxy</th>
                        <th>Windows Login</th>
                        <th>Password</th>
                        <th>Status</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {visibleAccounts.map((account) => (
                        <tr key={account.id}>
                            <td>{account.id}</td>
                            <td>{renderUser(account.user)}</td>
                            <td>{account.server ? `${account.server.host}:${account.server.port}` : ""}</td>
                            <td>{account.proxy ? `${account.proxy.host}:${account.proxy.port}` : ""}</td>
                            <td>{account.windows_username}</td>
                            <td>{account.windows_password}</td>
                            <td><Badge bg={badgeVariant(account.status)}>{account.status}</Badge></td>
                            <td className="text-nowrap">
                                {account.status !== "DELETED" && (
                                    <Button size="sm" variant="outline-success" className="me-1" onClick={() => createWindowsUser(account)} disabled={Boolean(actionKey)}>
                                        <ActionSpinner show={isAction(`account-create-${account.id}`)} />
                                        Create user
                                    </Button>
                                )}
                                {account.status !== "DELETED" && (
                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => lockdownWindowsUser(account)} disabled={Boolean(actionKey)}>
                                        <ActionSpinner show={isAction(`account-lockdown-${account.id}`)} />
                                        Lockdown
                                    </Button>
                                )}
                                {!["DISABLED", "DELETED"].includes(account.status) && (
                                    <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setAccountStatus(account, "DISABLED")} disabled={Boolean(actionKey)}>
                                        <ActionSpinner show={isAction(`account-disabled-${account.id}`)} />
                                        Disable
                                    </Button>
                                )}
                                {account.status !== "DELETED" && (
                                    <Button size="sm" variant="outline-danger" onClick={() => removeAccount(account)} disabled={Boolean(actionKey)}>
                                        <ActionSpinner show={isAction(`account-delete-${account.id}`)} />
                                        Delete
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                    {data.accounts.length === 0 && (
                        <tr><td colSpan={8} className="text-muted text-center">No RDP accounts yet.</td></tr>
                    )}
                    </tbody>
                </Table>
            </div>
            <PaginationControls page={accountPage} total={data.accounts.length} onPage={setAccountPage} />
        </div>
    );

    const renderServersAndProxies = () => (
        <Row className="g-2 mb-3">
            <Col lg={6}>
                <div className="border rounded p-2 h-100">
                    <div className="fw-semibold small mb-2">Servers</div>
                    <Row className="g-1 mb-2">
                        <Col md={3}><Form.Control size="sm" placeholder="Label" value={serverForm.label} onChange={(e) => setServerForm({ ...serverForm, label: e.target.value })} /></Col>
                        <Col md={3}><Form.Control size="sm" placeholder="Host" value={serverForm.host} onChange={(e) => setServerForm({ ...serverForm, host: e.target.value })} /></Col>
                        <Col md={2}><Form.Control size="sm" placeholder="Port" value={serverForm.port} onChange={(e) => setServerForm({ ...serverForm, port: e.target.value })} /></Col>
                        <Col md={2}>
                            <Form.Select size="sm" value={serverForm.status} onChange={(e) => setServerForm({ ...serverForm, status: e.target.value })}>
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="DISABLED">DISABLED</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Button size="sm" variant="success" className="w-100" onClick={saveServer} disabled={saving}>
                                <FaPlus className="me-1" />
                                {editingServerId ? "Save" : "Add"}
                            </Button>
                        </Col>
                        <Col xs={12}><Form.Control size="sm" placeholder="Notes" value={serverForm.notes} onChange={(e) => setServerForm({ ...serverForm, notes: e.target.value })} /></Col>
                    </Row>
                    <Table size="sm" bordered hover responsive className="mb-0 small">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Server</th>
                            <th>Status</th>
                            <th>Notes</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {data.servers.map((server) => (
                            <tr key={server.id}>
                                <td>{server.id}</td>
                                <td>{server.label}<br /><span className="text-muted">{server.host}:{server.port}</span></td>
                                <td><Badge bg={badgeVariant(server.status)}>{server.status}</Badge></td>
                                <td>{server.notes}</td>
                                <td><Button size="sm" variant="outline-primary" onClick={() => editServer(server)}>Edit</Button></td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            </Col>

            <Col lg={6}>
                <div className="border rounded p-2 h-100">
                    <div className="fw-semibold small mb-2">Proxies</div>
                    <Row className="g-1 mb-2">
                        <Col md={2}><Form.Control size="sm" placeholder="Label" value={proxyForm.label} onChange={(e) => setProxyForm({ ...proxyForm, label: e.target.value })} /></Col>
                        <Col md={3}><Form.Control size="sm" placeholder="Host" value={proxyForm.host} onChange={(e) => setProxyForm({ ...proxyForm, host: e.target.value })} /></Col>
                        <Col md={2}><Form.Control size="sm" placeholder="Port" value={proxyForm.port} onChange={(e) => setProxyForm({ ...proxyForm, port: e.target.value })} /></Col>
                        <Col md={2}><Form.Control size="sm" placeholder="Country" value={proxyForm.country} onChange={(e) => setProxyForm({ ...proxyForm, country: e.target.value })} /></Col>
                        <Col md={3}>
                            <Form.Select size="sm" value={proxyForm.status} onChange={(e) => setProxyForm({ ...proxyForm, status: e.target.value })}>
                                <option value="FREE">FREE</option>
                                <option value="ASSIGNED">ASSIGNED</option>
                                <option value="DISABLED">DISABLED</option>
                                <option value="BROKEN">BROKEN</option>
                            </Form.Select>
                        </Col>
                        <Col md={3}><Form.Control size="sm" placeholder="Proxy login" value={proxyForm.username} onChange={(e) => setProxyForm({ ...proxyForm, username: e.target.value })} /></Col>
                        <Col md={3}><Form.Control size="sm" placeholder="Proxy password" value={proxyForm.password} onChange={(e) => setProxyForm({ ...proxyForm, password: e.target.value })} /></Col>
                        <Col md={4}><Form.Control size="sm" placeholder="Notes" value={proxyForm.notes} onChange={(e) => setProxyForm({ ...proxyForm, notes: e.target.value })} /></Col>
                        <Col md={2}>
                            <Button size="sm" variant="success" className="w-100" onClick={saveProxy} disabled={saving}>
                                {editingProxyId ? "Save" : "Add"}
                            </Button>
                        </Col>
                    </Row>
                    <Table size="sm" bordered hover responsive className="mb-0 small">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Proxy</th>
                            <th>Status</th>
                            <th>Assigned</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {data.proxies.map((proxy) => {
                            const assigned = (proxy.accounts || []).find((a) => ["MANUAL_REQUIRED", "ACTIVE"].includes(a.status));
                            return (
                                <tr key={proxy.id}>
                                    <td>{proxy.id}</td>
                                    <td>{proxy.label || "Proxy"}<br /><span className="text-muted">{proxy.host}:{proxy.port} {proxy.country ? `(${proxy.country})` : ""}</span></td>
                                    <td><Badge bg={badgeVariant(proxy.status)}>{proxy.status}</Badge></td>
                                    <td>{assigned ? renderUser(assigned.user) : ""}</td>
                                    <td className="text-nowrap">
                                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => editProxy(proxy)}>Edit</Button>
                                        <Button size="sm" variant="outline-danger" onClick={() => removeProxy(proxy)} disabled={!!assigned || saving}>
                                            <FaTrash />
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                </div>
            </Col>
        </Row>
    );

    const renderManualRequest = () => (
        <div className="border rounded p-2 mb-3">
            <div className="fw-semibold small mb-2">Manual Request</div>
            <Row className="g-1 align-items-end">
                <Col md={2}>
                    <Form.Label className="small mb-1">Manual User ID</Form.Label>
                    <Form.Control size="sm" value={manualUserId} onChange={(e) => setManualUserId(e.target.value)} />
                </Col>
                <Col md={5}>
                    <Form.Label className="small mb-1">Admin note</Form.Label>
                    <Form.Control size="sm" value={manualNote} onChange={(e) => setManualNote(e.target.value)} />
                </Col>
                <Col md={2}>
                    <Button size="sm" variant="outline-success" className="w-100" onClick={createManualRequest} disabled={saving}>
                        Create request
                    </Button>
                </Col>
            </Row>
        </div>
    );

    const renderJobs = () => (
        <div className="border rounded p-2">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div>
                    <div className="fw-semibold small">Worker Jobs</div>
                    <div className="text-muted small">Dry-run previews actions. Run live applies them through WinRM.</div>
                </div>
                <Badge bg="success">LIVE READY</Badge>
            </div>
            <div className="rdp-admin-table">
                <Table size="sm" bordered hover striped responsive className="mb-0">
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Action</th>
                        <th>Status</th>
                        <th>User</th>
                        <th>Account</th>
                        <th>Target</th>
                        <th>Result</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {visibleJobs.map((job) => {
                        const steps = Array.isArray(job?.result?.steps) ? job.result.steps : [];
                        const resultText = steps.length > 0
                            ? steps.join(" / ")
                            : (job.error_message || job?.result?.message || "No worker result yet.");
                        return (
                            <tr key={job.id}>
                                <td>{job.id}</td>
                                <td>{job.action}</td>
                                <td>
                                    <Badge bg={badgeVariant(job.status)}>{job.status}</Badge>
                                    <div className="text-muted">{job.mode}</div>
                                </td>
                                <td>{renderUser(job.user)}</td>
                                <td>{job.account ? job.account.windows_username : ""}</td>
                                <td>
                                    {job.server ? `${job.server.host}:${job.server.port}` : ""}
                                    {job.proxy ? <div className="text-muted">Proxy {job.proxy.host}:{job.proxy.port}</div> : null}
                                </td>
                                <td style={{ minWidth: 220, maxWidth: 520 }} className={job.error_message ? "text-danger" : "text-muted"}>
                                    <div className="text-truncate" title={resultText}>{resultText}</div>
                                </td>
                                <td className="text-nowrap">
                                    <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => rerunJob(job)} disabled={Boolean(actionKey)}>
                                        <ActionSpinner show={isAction(`job-dry-${job.id}`)} />
                                        Dry-run
                                    </Button>
                                    <Button size="sm" variant="outline-success" className="me-1" onClick={() => runJobLive(job)} disabled={Boolean(actionKey)}>
                                        <ActionSpinner show={isAction(`job-live-${job.id}`)} />
                                        Live
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => removeJob(job)} disabled={Boolean(actionKey)}>
                                        <ActionSpinner show={isAction(`job-delete-${job.id}`)} />
                                        <FaTrash />
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                    {data.jobs.length === 0 && (
                        <tr><td colSpan={8} className="text-muted text-center">No worker jobs yet.</td></tr>
                    )}
                    </tbody>
                </Table>
            </div>
            <PaginationControls page={jobPage} total={data.jobs.length} onPage={setJobPage} />
        </div>
    );

    return (
        <div>
            {compactTableStyle}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <h5 className="text-muted mb-0">
                    <FaDesktop className="me-2 text-success" />
                    RDP Access Admin
                </h5>
                <Button size="sm" variant="outline-success" onClick={load} disabled={loading || saving}>
                    <FaRedo className="me-1" />
                    Refresh
                </Button>
            </div>

            {error && <Alert variant="danger" className="py-2">{error}</Alert>}
            {success && <Alert variant="success" className="py-2">{success}</Alert>}

            {loading ? (
                <div className="d-flex align-items-center gap-2 text-muted">
                    <Spinner size="sm" />
                    Loading RDP data...
                </div>
            ) : (
                <>
                    {renderRequests()}
                    {renderAccounts()}
                    {renderServersAndProxies()}
                    {renderManualRequest()}
                    {renderJobs()}
                </>
            )}
        </div>
    );
};

export default AdminRdpAccess;
